import { z } from "zod";
import { format as dateFormat } from "date-fns";
import { UTCDate } from "@date-fns/utc";
import { enUS, enGB, fr, frCA, de, es } from "date-fns/locale";

export const validationRules = [
  "auto",
  "none",
  "url",
  "email",
  "phone",
  "integer",
  "number",
  "positive",
  "nonnegative",
  "postal-us",
  "postal-ca",
  "uuid",
  "hex-color",
  "alphanumeric",
] as const;
export const policyReferenceSchema = z
  .object({
    id: z.string().min(1).max(200),
    version: z.number().int().positive(),
    name: z.string().min(1).max(200),
    scope: z.enum(["application", "site", "company", "project", "variable"]),
  })
  .strict();
export const validationSettingsSchema = z
  .object({
    maxLength: z.number().int().min(0).max(32768).optional(),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    pattern: z.string().max(256).optional(),
    mask: z.string().optional(),
    rule: z.enum(validationRules).optional(),
    preset: policyReferenceSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.min !== undefined && v.max !== undefined && v.min > v.max)
      ctx.addIssue({
        code: "custom",
        message: "Minimum cannot exceed maximum",
        path: ["min"],
      });
  });
const dateLocales = { "en-US": enUS, "en-GB": enGB, fr, "fr-CA": frCA, de, es };
export const supportedDateLocales = Object.keys(dateLocales);
export type NumberPattern = {
  useGrouping: boolean;
  minimumIntegerDigits: number;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  style: "decimal" | "percent" | "currency";
};
/** Standard decimal skeletons: #,##0.00, 0.##, 000000, 0.0%, ¤#,##0.00. */
export function parseNumberPattern(pattern: string): NumberPattern {
  const match = /^(¤)?(#,##0|0{1,20})(?:\.(0{0,20})(#{0,20}))?(%)?$/.exec(
    pattern,
  );
  if (
    !match ||
    (pattern.includes(".") && !(match[3]?.length || match[4]?.length)) ||
    (match[3]?.length || 0) + (match[4]?.length || 0) > 20 ||
    (match[1] && match[5])
  )
    throw new Error(
      "Use a standard number pattern such as #,##0.00, 0.##, 0000 or 0.0%",
    );
  return {
    useGrouping: match[2].includes(","),
    minimumIntegerDigits: match[2].includes(",") ? 1 : match[2].length,
    minimumFractionDigits: match[3]?.length || 0,
    maximumFractionDigits: (match[3]?.length || 0) + (match[4]?.length || 0),
    style: match[1] ? "currency" : match[5] ? "percent" : "decimal",
  };
}
export function assertDatePattern(pattern: string, locale = "en-US") {
  if (
    !pattern ||
    pattern.length > 128 ||
    !/[yYuUrQqMLwIdDecihHKkmsStaPp]/.test(pattern)
  )
    throw new Error(
      "Enter a date/time pattern, for example yyyy-MM-dd or h:mm a",
    );
  let quoted = false;
  for (let i = 0; i < pattern.length; i++)
    if (pattern[i] === "'") {
      if (pattern[i + 1] === "'") i++;
      else quoted = !quoted;
    }
  if (quoted) throw new Error("Close the quoted text in the date/time pattern");
  if (!Object.hasOwn(dateLocales, locale))
    throw new Error("Choose a supported date/time locale");
  dateFormat(new UTCDate("2024-02-29T13:05:07Z"), pattern, {
    locale: dateLocales[locale as keyof typeof dateLocales],
  });
}
export const formatSettingsSchema = z
  .object({
    locale: z.string().max(100).default("en-US"),
    prefix: z.string().max(1000).default(""),
    suffix: z.string().max(1000).default(""),
    uppercase: z.boolean().default(false),
    kind: z.enum(["raw", "text", "date", "number"]).optional(),
    datePattern: z.string().max(128).optional(),
    numberPattern: z.string().max(128).optional(),
    currency: z.string().max(3).optional(),
    casing: z.enum(["unchanged", "lowercase", "title"]).optional(),
    trim: z.boolean().optional(),
    preset: policyReferenceSchema.optional(),
  })
  .superRefine((v, ctx) => {
    try {
      if (v.kind || v.casing) new Intl.NumberFormat(v.locale);
      if (v.kind === "date") assertDatePattern(v.datePattern || "", v.locale);
      if (v.kind === "number") {
        const options = parseNumberPattern(v.numberPattern || "");
        if (
          options.style === "currency" &&
          !/^[A-Z]{3}$/.test(v.currency || "")
        )
          throw new Error("Choose a three-letter currency code such as USD");
        new Intl.NumberFormat(v.locale, {
          ...options,
          ...(options.style === "currency" ? { currency: v.currency } : {}),
        });
      }
    } catch (error) {
      ctx.addIssue({
        code: "custom",
        message: (error as Error).message,
        path: [v.kind === "date" ? "datePattern" : "numberPattern"],
      });
    }
  });
export type ValidationSettings = z.infer<typeof validationSettingsSchema>;
export type FormatSettings = z.infer<typeof formatSettingsSchema>;
export type PolicyField = {
  default?: unknown;
  type: string;
  key: string;
  label: string;
  validation: ValidationSettings;
  format: FormatSettings;
};
export function isWebsiteField(
  field: Pick<PolicyField, "type" | "key" | "label">,
) {
  return (
    ["text", "textarea"].includes(field.type) &&
    [field.key, field.label].some((value) =>
      /^(?:website|web[ _-]?address|web[ _-]?url|site[ _-]?url|url|homepage)$|(?:[ _-](?:website|url))$/i.test(
        value.trim(),
      ),
    )
  );
}
export function validWebsite(value: string) {
  if (value !== value.trim() || /\s|[\u0000-\u001f\u007f]/.test(value))
    return false;
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && !!url.hostname;
  } catch {
    return false;
  }
}
export function strictDate(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number),
    date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  date.setUTCHours(0, 0, 0, 0);
  return (
    year >= 1 &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}
export function strictTime(value: string) {
  return /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?$/.test(value);
}
export function strictDateTime(value: string) {
  const m =
    /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)(Z|[+-]\d{2}:\d{2})?$/.exec(
      value,
    );
  return (
    !!m &&
    strictDate(m[1]) &&
    strictTime(m[2]) &&
    Number.isFinite(Date.parse(value + (m[3] ? "" : "Z")))
  );
}
const decimal = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;
export function numericValue(value: unknown) {
  return typeof value === "number"
    ? value
    : typeof value === "string" && decimal.test(value.trim())
      ? Number(value)
      : NaN;
}
/** Optional empty values are handled by the form's required flag before this. */
export function policyValueError(
  field: PolicyField,
  value: unknown,
): string | undefined {
  const rule = field.validation.rule || "auto",
    s = String(value);
  if (field.format.kind === "number" && !Number.isFinite(numericValue(value)))
    return "enter a finite decimal number for this format";
  if (
    field.format.kind === "date" &&
    !strictDate(s) &&
    !strictDateTime(s) &&
    !strictTime(s)
  )
    return "enter a valid ISO date, date/time or time for this format";
  const effective =
    rule === "auto"
      ? field.type === "zip"
        ? "postal-us"
        : field.type === "uri"
          ? "uri"
          : field.type === "phone"
            ? "phone"
            : isWebsiteField(field)
              ? "url"
              : "none"
      : rule;
  // Importers retain source tokens as editable defaults. Automatic name inference must
  // preserve that artwork until configured; explicit URL policies always validate it.
  if (
    ["url", "uri", "postal-us", "phone"].includes(effective) &&
    rule === "auto" &&
    value === field.default &&
    (s === `<${field.key}>` || s === `<${field.label}>`)
  )
    return;
  switch (effective) {
    case "uri":
      try {
        const uri = new URL(s);
        return ["http:", "https:", "mailto:", "tel:", "ftp:", "urn:"].includes(
          uri.protocol,
        ) &&
          !/\s/.test(s) &&
          !!uri.pathname
          ? undefined
          : "enter a supported complete URI";
      } catch {
        return "enter a complete URI, such as https://example.com";
      }
    case "url":
      return validWebsite(s) ||
        (rule === "auto" &&
          /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,63}(?::[0-9]+)?(?:[/?#].*)?$/i.test(
            s,
          ) &&
          validWebsite("https://" + s))
        ? undefined
        : "enter a complete HTTP or HTTPS website URL";
    case "email":
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)
        ? undefined
        : "enter a valid email address";
    case "phone":
      return /^\+?[0-9() .-]{7,32}$/.test(s) && s.replace(/\D/g, "").length >= 7
        ? undefined
        : "enter a valid phone number";
    case "number":
    case "integer":
    case "positive":
    case "nonnegative": {
      const n = numericValue(value);
      if (!Number.isFinite(n)) return "enter a finite decimal number";
      if (effective === "integer" && !Number.isSafeInteger(n))
        return "enter a safe whole number";
      if (effective === "positive" && n <= 0)
        return "enter a number greater than zero";
      if (effective === "nonnegative" && n < 0)
        return "enter zero or a positive number";
      if (field.validation.min !== undefined && n < field.validation.min)
        return `minimum ${field.validation.min}`;
      if (field.validation.max !== undefined && n > field.validation.max)
        return `maximum ${field.validation.max}`;
      return;
    }
    case "postal-us":
      return /^\d{5}(?:-\d{4})?$/.test(s)
        ? undefined
        : "enter a five-digit ZIP code or ZIP+4";
    case "postal-ca":
      return /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i.test(
        s,
      )
        ? undefined
        : "enter a Canadian postal code";
    case "uuid":
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        s,
      )
        ? undefined
        : "enter a UUID";
    case "hex-color":
      return /^#[0-9a-f]{6}$/i.test(s)
        ? undefined
        : "enter a six-digit hex color";
    case "alphanumeric":
      return /^[\p{L}\p{N}]+$/u.test(s)
        ? undefined
        : "use letters and numbers only";
  }
}
export function formattedPolicyValue(
  field: PolicyField,
  value: unknown,
  plain: string,
) {
  const f = field.format;
  let text = plain;
  if (f.kind === "number") {
    const n = numericValue(value);
    if (Number.isFinite(n))
      text = new Intl.NumberFormat(f.locale, {
        ...parseNumberPattern(f.numberPattern || "0"),
        ...(f.currency ? { currency: f.currency } : {}),
      }).format(n);
  } else if (f.kind === "date") {
    const s = String(value);
    let iso: string | undefined;
    if (strictDate(s)) iso = s + "T00:00:00Z";
    else if (strictTime(s)) iso = "1970-01-01T" + s + "Z";
    else if (strictDateTime(s))
      iso = s + (/(?:Z|[+-]\d{2}:\d{2})$/.test(s) ? "" : "Z");
    if (iso)
      text = dateFormat(new UTCDate(iso), f.datePattern || "yyyy-MM-dd", {
        locale: dateLocales[f.locale as keyof typeof dateLocales] || enUS,
      });
  }
  if (f.trim) text = text.trim();
  if (f.casing === "lowercase") text = text.toLocaleLowerCase(f.locale);
  if (f.casing === "title")
    text = text.replace(
      /\p{L}[\p{L}\p{M}]*/gu,
      (word) =>
        word[0].toLocaleUpperCase(f.locale) +
        word.slice(1).toLocaleLowerCase(f.locale),
    );
  return text;
}
